/**
 * Currency helpers for Indonesian format.
 *
 * `unit_price` can carry decimals expressed with a comma (e.g. "1.500,50").
 * Integer part uses `.` as thousand separator, decimal part uses `,`.
 *
 * Display rules:
 *   - Integer values render without decimals: `1.500`
 *   - Decimal values render with up to 4 fractional digits, no trailing zeros: `1.500,5`, `1.500,1234`
 */

const MAX_DECIMALS = 4;
const QTY_DECIMALS = 2;

/**
 * Parse a user-entered number string into a float rounded to `maxDecimals`.
 * Accepts Indonesian ("1.500,50"), plain ("1500.5") or numeric inputs.
 */
export function parseDecimalInput(value, maxDecimals = MAX_DECIMALS) {
  const factor = 10 ** maxDecimals;
  if (value === '' || value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return 0;
    return Math.round(value * factor) / factor;
  }
  let str = String(value).trim();
  if (!str) return 0;
  // If both '.' and ',' present, treat '.' as thousand separator and ',' as decimal.
  // If only '.', keep it as decimal ONLY when it's clearly a decimal marker (single dot with <=4 trailing digits).
  const hasComma = str.includes(',');
  if (hasComma) {
    str = str.replace(/\./g, '').replace(',', '.');
  } else {
    // Ambiguous "1.500" — user might mean 1500 (thousand sep). Keep as thousand sep if it splits into 3-digit groups.
    const parts = str.split('.');
    const looksLikeThousandSep =
      parts.length > 1 && parts.slice(1).every((p) => p.length === 3);
    if (looksLikeThousandSep) str = parts.join('');
  }
  const num = parseFloat(str);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * factor) / factor;
}

/**
 * Format the string a user is currently typing so thousand separators are
 * applied to the integer portion while a trailing comma / decimal digits
 * are preserved. Caps the decimal part at `maxDecimals` digits.
 */
export function formatDecimalInput(raw, maxDecimals = MAX_DECIMALS) {
  if (raw === '' || raw === null || raw === undefined) return '';
  // Keep only digits and commas — dots will be reinserted as thousand seps.
  let cleaned = String(raw).replace(/[^\d,]/g, '');
  // Keep only the first comma.
  const commaIdx = cleaned.indexOf(',');
  if (commaIdx !== -1) {
    cleaned =
      cleaned.slice(0, commaIdx + 1) +
      cleaned.slice(commaIdx + 1).replace(/,/g, '');
  }
  const [intPart = '', decPart] = cleaned.split(',');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  // A whole-number field has nowhere to put a comma; dropping it beats leaving
  // a dangling separator the user can never fill in.
  if (decPart === undefined || maxDecimals === 0) return intFormatted;
  return `${intFormatted},${decPart.slice(0, maxDecimals)}`;
}

/**
 * Display a number in Indonesian locale with no trailing zeros.
 * Used for tables, print, voucher, receipts.
 */
export function formatDecimalID(value, maxDecimals = MAX_DECIMALS) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('id-ID', { maximumFractionDigits: maxDecimals });
}

// ── Prices: up to 4 decimals ────────────────────────────────────────────────
export const parseUnitPrice = (value) => parseDecimalInput(value, MAX_DECIMALS);
export const formatUnitPriceInput = (raw) => formatDecimalInput(raw, MAX_DECIMALS);
export const formatCurrencyID = (value) => formatDecimalID(value, MAX_DECIMALS);

// ── Signed fields (pembulatan): may be negative ─────────────────────────────
// A controlled `type="number"` input cannot hold a lone "-": the browser
// reports an empty value for it, so the minus never reaches state and the user
// has to type the digits first. These keep the sign as part of the display
// string instead, so it can be typed in any order.

export function formatSignedInput(raw, maxDecimals = MAX_DECIMALS) {
  if (raw === '' || raw === null || raw === undefined) return '';
  const str = String(raw);
  const negative = str.trim().startsWith('-');
  const body = formatDecimalInput(str.replace(/-/g, ''), maxDecimals);
  if (!body) return negative ? '-' : '';
  return negative ? `-${body}` : body;
}

export function parseSignedInput(value, maxDecimals = MAX_DECIMALS) {
  if (value === '' || value === null || value === undefined) return 0;
  const str = String(value);
  const negative = str.trim().startsWith('-');
  const magnitude = parseDecimalInput(str.replace(/-/g, ''), maxDecimals);
  // A lone "-" carries no magnitude yet; keep it 0 rather than -0.
  if (magnitude === 0) return 0;
  return negative ? -magnitude : magnitude;
}

/** Display a possibly-negative amount, e.g. "-1.500" for a pembulatan. */
export function formatSignedID(value, maxDecimals = MAX_DECIMALS) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return '';
  return `${num < 0 ? '-' : ''}${formatDecimalID(Math.abs(num), maxDecimals)}`;
}

// ── Quantities: same Indonesian formatting, capped at 2 decimals ────────────
// Previously the qty input stripped its own thousand separators while typing,
// and its display formatter inserted dots without regard for the decimal part,
// rendering 1000.5 as the ambiguous "1.000.5".
export const parseQuantity = (value) => parseDecimalInput(value, QTY_DECIMALS);
export const formatQuantityInput = (raw) => formatDecimalInput(raw, QTY_DECIMALS);
export const formatQuantity = (value) => formatDecimalID(value, QTY_DECIMALS);


